//External
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
//Internal
import { useMobilePanelOverlay } from "./useMobilePanelOverlay";

/*******************
@Purpose : Shared open panel for the filter pills (Categories, Brands, Price
           Range, Seating Capacity, Colors). Desktop keeps the panel inline,
           position:absolute under its own trigger. Below $breakpoint-lg it's
           rendered through a portal into document.body instead of staying a
           descendant of .results-filters - that row is a horizontal scroller,
           and iOS Safari positions/clips position:fixed descendants of a
           touch scroller against the scroller itself rather than the viewport,
           which flattened the panel to nothing inside the 38px-tall chip row
           (the pills looked like they did nothing when tapped).

           Since the portalled panel is no longer inside the trigger's
           subtree, the outside-click close has to check the panel too, and it
           listens on pointerdown so a tap closes it on touch the same way a
           click does with a mouse.
@Parameter : {isOpen, anchorRef, onOutsideClose, className, children}
@Author : INIC
******************/
/*******************
@Purpose : Empty state for a filter dropdown's option list, shown when every
           option would return 0 cars given the other currently-active
           filters (see useUnavailableOptions) - rather than leaving the
           list blank when the user opens it.
@Author : INIC
******************/
export function FilterEmptyOptions() {
  return (
    <div className="filter-options-empty">
      <p className="filter-options-empty-title">No options left</p>
      <p className="filter-options-empty-subtitle">
        Your other filters don&apos;t leave any to choose from.
      </p>
    </div>
  );
}

function FilterDropdownPanel({ isOpen, anchorRef, onOutsideClose, className = "", children }) {
  const panelRef = useRef();
  const onOutsideCloseRef = useRef(onOutsideClose);
  const mobileOverlayStyle = useMobilePanelOverlay(isOpen, anchorRef);

  useEffect(() => {
    onOutsideCloseRef.current = onOutsideClose;
  }, [onOutsideClose]);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsidePointerDown = (e) => {
      const insideTrigger = anchorRef.current && anchorRef.current.contains(e.target);
      const insidePanel = panelRef.current && panelRef.current.contains(e.target);
      if (!insideTrigger && !insidePanel) {
        onOutsideCloseRef.current();
      }
    };

    document.addEventListener("pointerdown", handleOutsidePointerDown);
    return () => document.removeEventListener("pointerdown", handleOutsidePointerDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const panel = (
    <div
      ref={panelRef}
      className={`filter-dropdown-panel ${className} ${mobileOverlayStyle ? "filter-dropdown-panel--mobile-overlay" : ""}`}
      style={mobileOverlayStyle || undefined}
    >
      {children}
    </div>
  );

  return mobileOverlayStyle ? createPortal(panel, document.body) : panel;
}

export default FilterDropdownPanel;
